import React from 'react'
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Legend,
  Tooltip,
} from 'recharts'
import { Box, Typography } from '@mui/material'
import { useAppSelector } from '../../hooks/redux'

const COLORS = {
  pending: '#ff9800',
  in_progress: '#2196f3',
  completed: '#4caf50',
  cancelled: '#f44336',
}

const GRADIENT_COLORS = {
  pending: 'url(#pendingGradient)',
  in_progress: 'url(#inProgressGradient)',
  completed: 'url(#completedGradient)',
  cancelled: 'url(#cancelledGradient)',
}

const STATUS_LABELS = {
  pending: 'Pending',
  in_progress: 'In Progress',
  completed: 'Completed',
  cancelled: 'Cancelled',
}

export const OrderStatusChart: React.FC = () => {
  const { dashboardMetrics } = useAppSelector((state) => state.realtime)

  // Mock data - in real app, this would come from the API
  const data = [
    {
      name: STATUS_LABELS.pending,
      value: Math.max(0, dashboardMetrics.totalOrders - dashboardMetrics.activeOrders - dashboardMetrics.completedOrders),
      status: 'pending',
    },
    {
      name: STATUS_LABELS.in_progress,
      value: dashboardMetrics.activeOrders || 0,
      status: 'in_progress',
    },
    {
      name: STATUS_LABELS.completed,
      value: dashboardMetrics.completedOrders || 0,
      status: 'completed',
    },
    {
      name: STATUS_LABELS.cancelled,
      value: 5, // Mock data
      status: 'cancelled',
    },
  ].filter(item => item.value > 0)

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0]
      return (
        <Box
          sx={{
            background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(255, 255, 255, 0.9) 100%)',
            backdropFilter: 'blur(20px)',
            p: 2.5,
            border: '1px solid',
            borderColor: 'rgba(0, 0, 0, 0.08)',
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
          }}
        >
          <Typography variant="body1" sx={{ fontWeight: 700, mb: 1, color: data.payload.fill }}>
            {data.payload.name}
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.primary' }}>
            Count: {data.value}
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
            Percentage: {((data.value / dashboardMetrics.totalOrders) * 100).toFixed(1)}%
          </Typography>
        </Box>
      )
    }
    return null
  }

  const CustomLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }: any) => {
    if (percent < 0.05) return null // Don't show labels for slices smaller than 5%
    
    const RADIAN = Math.PI / 180
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5
    const x = cx + radius * Math.cos(-midAngle * RADIAN)
    const y = cy + radius * Math.sin(-midAngle * RADIAN)

    return (
      <text
        x={x}
        y={y}
        fill="white"
        textAnchor={x > cx ? 'start' : 'end'}
        dominantBaseline="central"
        fontSize={12}
        fontWeight={600}
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    )
  }

  if (data.length === 0) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          color: 'text.secondary',
        }}
      >
        <Typography variant="body2">No order data available</Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ width: '100%', height: '100%', position: 'relative' }}>
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <defs>
            <linearGradient id="pendingGradient" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#ff9800" stopOpacity={1} />
              <stop offset="100%" stopColor="#f57c00" stopOpacity={1} />
            </linearGradient>
            <linearGradient id="inProgressGradient" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#2196f3" stopOpacity={1} />
              <stop offset="100%" stopColor="#1976d2" stopOpacity={1} />
            </linearGradient>
            <linearGradient id="completedGradient" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#4caf50" stopOpacity={1} />
              <stop offset="100%" stopColor="#388e3c" stopOpacity={1} />
            </linearGradient>
            <linearGradient id="cancelledGradient" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#f44336" stopOpacity={1} />
              <stop offset="100%" stopColor="#d32f2f" stopOpacity={1} />
            </linearGradient>
          </defs>
          <Pie
            data={data}
            cx="50%"
            cy="50%"
            labelLine={false}
            label={CustomLabel}
            outerRadius={90}
            innerRadius={50}
            fill="#8884d8"
            dataKey="value"
            paddingAngle={3}
            animationBegin={0}
            animationDuration={800}
          >
            {data.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={GRADIENT_COLORS[entry.status as keyof typeof GRADIENT_COLORS]}
                stroke="white"
                strokeWidth={2}
              />
            ))}
          </Pie>
          <Tooltip content={<CustomTooltip />} />
          <Legend
            verticalAlign="bottom"
            height={40}
            iconType="circle"
            formatter={(value) => (
              <span style={{
                fontSize: '14px',
                fontWeight: 600,
              }}>
                {value}
              </span>
            )}
          />
        </PieChart>
      </ResponsiveContainer>
    </Box>
  )
}
